import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import API from "@/api/API";
import Papa from "papaparse";
import { TEST_MANAUL_UPLOAD_PROJECTS } from "@/constants";

export const loadCamera = function () {
  const loader = new GLTFLoader();
  loader.setPath("/assets/model/");
  loader.load("camera.glb", (gltf) => {
    this.cameraObject = gltf.scene.children[0];
  });
};

export const createCameraInstancedMesh = function (instanceCount) {
  const cameraGeometry = this.cameraObject.geometry;
  const cameraMaterial = this.cameraObject.material;
  const instancedMesh = new THREE.InstancedMesh(
    cameraGeometry,
    cameraMaterial,
    instanceCount
  );
  instancedMesh.visible = false;

  this.scene.add(instancedMesh);

  return instancedMesh;
};

export const transformCameraFromMatrix = function (row) {
  const m = new THREE.Matrix4()
    .fromArray([].concat(...row.transformationMatrix))
    .transpose();

  let axisRot = this.exportedManually ? Math.PI / 2 : -Math.PI / 2;
  if (TEST_MANAUL_UPLOAD_PROJECTS.includes(this.projectId)) axisRot = 0;

  const axisFix = new THREE.Matrix4().makeRotationX(axisRot);
  const modelMatrix = this.modelObject.children[0].matrixWorld;
  const c2w = new THREE.Matrix4()
    .multiplyMatrices(axisFix, m)
    .premultiply(modelMatrix);

  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  c2w.decompose(position, quaternion, scale);

  const finalMatrix = new THREE.Matrix4();
  finalMatrix.compose(position, quaternion, new THREE.Vector3(5, 5, 5));
  finalMatrix.multiply(this.cameraObject.matrix);
  return finalMatrix;
};

export const transformCamera = function (row) {
  const finalMatrix = new THREE.Matrix4();
  const positionMatrix = new THREE.Vector3();
  const quaternionMatrix = new THREE.Quaternion();

  const scaleMatrix = new THREE.Vector3(5, 5, 5);

  const relativePosition = new THREE.Vector3(row.x, row.y, row.alt);

  let defaultXRotation = Math.PI / 2;

  if (this.exportedManually) {
    defaultXRotation = -Math.PI / 2;
  }

  if (TEST_MANAUL_UPLOAD_PROJECTS.includes(this.projectId))
    defaultXRotation = 0;

  const rotationMatrix = new THREE.Matrix4();
  rotationMatrix.makeRotationX(defaultXRotation);
  relativePosition.applyMatrix4(rotationMatrix);

  const modelMatrix = new THREE.Matrix4();
  modelMatrix.copy(this.modelObject.children[0].matrixWorld);

  positionMatrix.copy(relativePosition).applyMatrix4(modelMatrix);

  const pitchRadians = THREE.MathUtils.degToRad(row.pitch);
  const headingRadians = THREE.MathUtils.degToRad(this.getCameraHeading(row));
  const rollRadians = THREE.MathUtils.degToRad(row.roll);

  const cameraEuler = new THREE.Euler(
    pitchRadians,
    rollRadians - 5,
    -headingRadians,
    "ZXY"
  );

  const cameraQuaternion = new THREE.Quaternion().setFromEuler(cameraEuler);

  const modelQuaternion = new THREE.Quaternion();
  this.modelObject.children[0].getWorldQuaternion(modelQuaternion);

  const xRotation = new THREE.Quaternion();
  xRotation.setFromAxisAngle(new THREE.Vector3(1, 0, 0), defaultXRotation);

  quaternionMatrix
    .copy(modelQuaternion)
    .multiply(xRotation)
    .multiply(cameraQuaternion);

  finalMatrix.compose(positionMatrix, quaternionMatrix, scaleMatrix);
  finalMatrix.multiply(this.cameraObject.matrix);

  return finalMatrix;
};

export const fetchCameras = async function () {
  if (this.cameraMesh) return;

  if (!this.cameraPositionsCsvUrl) {
    const fastFusionModel = await API.projects.getFastFusionModel(
      this.projectId
    );
    this.cameraPositionsCsvUrl = fastFusionModel?.data?.camPosUrl;
    if (!this.cameraPositionsCsvUrl) {
      this.csvAvailable = false;
      return;
    }
  }

  try {
    const response = await fetch(this.cameraPositionsCsvUrl);
    if (!response.ok) {
      this.csvAvailable = false;
      return;
    }

    const csvText = await response.text();
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      complete: (results) => {
        const filteredResults = results.data.filter(
          (row) =>
            row.x && row.y && row.alt && row.pitch && (row.heading || row.yaw) && row.roll
        );
        if (filteredResults.length === 0) {
          this.csvAvailable = false;
          return;
        }
        this.cameraMesh = this.createCameraInstancedMesh(
          filteredResults.length
        );

        filteredResults.forEach((row, i) => {
          const cameraMatrix = this.transformCamera(row);
          this.cameraMesh.setMatrixAt(i, cameraMatrix);
        });
        this.camerasCsv = filteredResults;

        this.cameraMesh.instanceMatrix.needsUpdate = true;
      },
    });
  } catch (error) {
    console.error("Error fetching or parsing CSV:", error);
  }
};

export const setupCameraMesh = async function () {
  if (this.cameraMesh) return true;

  const categoryId = this.project?.technicalCategoryId;
  const baseResponse = await API.airteam3DViewer.getCameras(this.projectId, { mode: "base", categoryId });
  const baseData = baseResponse?.data;

  // BE returns `{ source: "transform" | "csv", results: [...] }` after the
  // unified-endpoint migration. Older deployments returned a bare array; that
  // legacy shape is treated as `source: "csv"`.
  let rawResults = [];
  let source = null;
  if (Array.isArray(baseData)) {
    rawResults = baseData;
    source = "csv";
  } else if (baseData && Array.isArray(baseData.results)) {
    rawResults = baseData.results;
    source = baseData.source ?? "csv";
  }

  if (source === "transform") {
    const transformRows = rawResults.filter(
      (row) => row.transformationMatrix && row.flY && row.w && row.h
    );
    if (transformRows.length === 0) return false;
    this.cameraMesh = this.createCameraInstancedMesh(transformRows.length);
    transformRows.forEach((row, i) => {
      const cameraMatrix = this.transformCameraFromMatrix(row);
      this.cameraMesh.setMatrixAt(i, cameraMatrix);
    });
    this.cameraTransforms = transformRows;
    this.cameraSource = "transform";
    this.cameraMesh.instanceMatrix.needsUpdate = true;
    return true;
  }

  const baseCameras = rawResults.filter(
    (cam) =>
      cam.x != null &&
      cam.y != null &&
      cam.alt != null &&
      cam.pitch != null &&
      (cam.heading != null || cam.yaw != null) &&
      cam.roll != null
  );

  if (baseCameras.length === 0) return false;

  this.cameraMesh = this.createCameraInstancedMesh(baseCameras.length);
  baseCameras.forEach((row, i) => {
    const cameraMatrix = this.transformCamera(row);
    this.cameraMesh.setMatrixAt(i, cameraMatrix);
  });
  this.camerasCsv = baseCameras;
  this.cameraSource = "csv";
  this.cameraMesh.instanceMatrix.needsUpdate = true;
  return true;
};

export const fetchCamerasWithImages = async function (page = 1, pageSize = 20) {
  try {
    const categoryId = this.project?.technicalCategoryId;

    if (page === 1 && !this.cameraMesh) {
      const hasCameras = await this.setupCameraMesh();
      if (!hasCameras) return null;
      this.camerasLoadedWithImages = true;
    }

    // Fetch the paginated images for this page
    const response = await API.airteam3DViewer.getCameras(this.projectId, { page, pageSize, categoryId });
    const data = response?.data;

    if (!data?.results || data.results.length === 0) {
      if (page === 1) return null;
      return { results: [], count: data?.count || 0 };
    }

    return {
      results: data.results.map((cam) => cam.projectFile),
      count: data.count,
    };
  } catch (error) {
    console.error("Error fetching cameras with images:", error);
    return null;
  }
};

export const displayCameras = async function () {
  const hasCameras = await this.setupCameraMesh();
  if (!hasCameras) {
    this.csvAvailable = false;
    return;
  }
  this.changeCamerasVisibility(true);
};

export const changeCamerasVisibility = function (visible) {
  this.cameraMesh.visible = visible;

  if (visible && this.cameraMesh) {
    this.cameraControls.dollyTo(120, true);
  }
};

export const getCameraHeading = function (camera) {
  return camera.heading ? camera.heading : camera.yaw;
}
